import React, { useEffect, useState } from 'react';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { DateTime } from 'luxon';
import { DateTimeRange } from '../../clients/quipli-service/report/report';
import { Button } from '../../components/form/button';
import Icon from '../../components/icons/icon';
import { DatePicker } from '../../components/date-picker/date-picker';
import { Link, useSearchParams } from 'react-router-dom';
import FileSaver from 'file-saver';
import { Card } from '../../components/card/card';
import { Spinner } from '../../components/spinner/spinner';
import { displayMoneyFormat } from '../../util/util';
import { Location } from '../../clients/quipli-service/location/location';
import {
  AccountTotalsRequest,
  AccountTotalsRow,
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
} from '../../clients/quipli-service/accounting/accounting';
import { AccountModal } from './account-modal';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import { ConfirmModal } from '../../components/modal/confirm-modal';
import { toast } from '../../util/toasts';
import useLocalStorage from 'use-local-storage';
import { SelectForm } from '../../components/select-form/select-form';
import { Toggle } from '../../components/form/toggle';
import { ButtonGroup } from '../../components/button-group/button-group';
import { SpinnerModal } from '../../components/modal/spinner-modal';

function extractAccountTotalsReportSearchParams(params: URLSearchParams): AccountTotalsRequest {
  const from = params.get('from') ? DateTime.fromISO(params.get('from')!) : undefined;
  const to = params.get('to') ? DateTime.fromISO(params.get('to')!) : undefined;
  const accountType = params.get('accountType') ? (params.get('accountType')! as GeneralLedgerAccountType) : undefined;
  const hidePerLocation = params.get('hidePerLocation') === 'true';
  const basis = (params.get('basis') as 'cash' | 'accrual') || 'accrual';
  return { from, to, accountType, hidePerLocation, basis };
}

function injectPaymentReportSearchParams(params: URLSearchParams, req: AccountTotalsRequest): URLSearchParams {
  req.from ? params.set('from', req.from.toISO()) : params.delete('from');
  req.to ? params.set('to', req.to.toISO()) : params.delete('to');
  req.accountType ? params.set('accountType', req.accountType) : params.delete('accountType');
  req.basis ? params.set('basis', req.basis) : params.set('basis', 'accrual');
  req.hidePerLocation
    ? params.set('hidePerLocation', req.hidePerLocation.toString())
    : params.delete('hidePerLocation');

  return params;
}

export const AccountTotals = () => {
  const { getAvailableAccountTotalsRange, accountTotals, exportAccountTotals, listAccounts, deleteAccount } =
    useAccountingApiV1();
  const { getMultipleConfigurations, listLocations } = useAPI();
  const [timezone, setTimezone] = useState<string>();
  const [currency, setCurrency] = useState<string>();
  const [report, setReport] = useState<AccountTotalsRow[]>();
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportRequest, setReportRequest] = useState<AccountTotalsRequest>(
    extractAccountTotalsReportSearchParams(searchParams),
  );
  const [availableAccountTotalsReportRange, setAvailableAccountTotalsReportRange] = useState<DateTimeRange>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accountModalOpen, setAccountModalOpen] = useState<boolean>(false);
  const [accountToUpdate, setAccountToUpdate] = useState<GeneralLedgerAccount | undefined>(undefined);
  const [accountToDelete, setAccountToDelete] = useState<GeneralLedgerAccount | undefined>(undefined);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [locations, setLocations] = useState<Location[]>();

  useEffect(() => {
    setReportRequest(extractAccountTotalsReportSearchParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    loadTimezone();
    getAvailableAccountTotalsRange().then(setAvailableAccountTotalsReportRange);
  }, []);

  const loadTimezone = async () => {
    const [confs, locations] = await Promise.all([
      getMultipleConfigurations(['CONF_TIMEZONE', 'CONF_CURRENCY']),
      listLocations(),
    ]);
    const timezone = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
    const currency = confs.find((c) => c.variable === 'CONF_CURRENCY')?.value;
    setCurrency(currency || 'USD');
    setTimezone(timezone);
    setLocations(locations);
    setReportRequest((rr) => {
      if (!reportRequest.from && !reportRequest.to) {
        return {
          ...rr,
          from: DateTime.local({ zone: timezone }).startOf('month'),
          to: DateTime.local({ zone: timezone }).endOf('month'),
        };
      } else {
        return rr;
      }
    });
  };

  async function loadReport() {
    if (timezone && Object.keys(reportRequest).length > 0) {
      const previousSearchString = searchParams.toString();
      const newParams = injectPaymentReportSearchParams(searchParams, reportRequest);
      if (previousSearchString !== newParams.toString()) {
        setSearchParams(newParams);
      }
      setIsLoading(true);
      const [report, accounts] = await Promise.all([
        accountTotals(reportRequest),
        listAccounts(true, reportRequest.accountType),
      ]);
      setReport(report);
      setAccounts(accounts);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
  }, [reportRequest, timezone]);

  const exportReport = async () => {
    if (reportRequest) {
      setIsExporting(true);
      try {
        const file = await exportAccountTotals({
          from: reportRequest.from,
          to: reportRequest.to,
          accountType: reportRequest.accountType,
          hidePerLocation: reportRequest.hidePerLocation,
          basis: reportRequest.basis,
        });

        const exportFileName = 'Account_Totals_Report';
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileExtension = '.xlsx';

        const data = new Blob([file], { type: fileType });
        FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
      } catch (e: any) {
        console.error(e);
        toast('Error exporting report', 'error');
      } finally {
        setIsExporting(false);
      }
    }
  };

  const reportingMonths: DateTimeRange[] = [];
  if (availableAccountTotalsReportRange) {
    let currentStart = availableAccountTotalsReportRange.from.startOf('month');
    while (currentStart.diffNow().milliseconds < 0) {
      reportingMonths.push({ from: currentStart, to: currentStart.endOf('month') });
      currentStart = currentStart.plus({ months: 1 }).setZone(timezone, { keepLocalTime: true });
    }
  }
  reportingMonths.reverse();
  let selectedMonth = -1;
  if (reportRequest.from && reportRequest.to) {
    selectedMonth = reportingMonths.findIndex(
      (m) => m.from.diff(reportRequest.from!).milliseconds === 0 && m.to.diff(reportRequest.to!).milliseconds === 0,
    );
  }

  const accountTypeOptions: { label: string; value?: GeneralLedgerAccountType }[] = [
    { label: 'All', value: undefined },
    { label: 'Tax', value: GeneralLedgerAccountType.Tax },
    { label: 'Income', value: GeneralLedgerAccountType.Income },
    { label: 'Expense', value: GeneralLedgerAccountType.Expense },
  ];
  let selectedAccountType;
  if (reportRequest.accountType) {
    selectedAccountType = accountTypeOptions.find((opt) => opt.value === reportRequest.accountType)!;
  }
  const buttonItems: { label: string; value: 'cash' | 'accrual' }[] = [
    { label: 'Cash', value: 'cash' },
    { label: 'Accrual', value: 'accrual' },
  ];

  return (
    <div className="account-totals container-v2 p-2 flex flex-column gap-2">
      <div className="flex justify-between items-center">
        <h1 className="m-0">Accounts</h1>
      </div>
      {reportRequest && (
        <div className="tax-report flex flex-column gap-4">
          <div className="flex flex-wrap justify-between items-end gap-4">
            <div className="flex flex-wrap items-end gap-4 items-stretch">
              <select
                className="h-fit text-[14px] self-end"
                value={selectedMonth}
                data-cy="reporting-month-select"
                required={false}
                onChange={(e) => {
                  const range = reportingMonths[parseInt(e.target.value)];
                  setReportRequest((prev) => ({
                    ...prev,
                    from: range.from,
                    to: range.to,
                  }));
                }}
              >
                {selectedMonth < 0 && <option value={-1}></option>}
                {reportingMonths.map((m, i) => (
                  <option key={i} value={i}>
                    {m.from.toFormat('LLLL yyyy')}
                  </option>
                ))}
              </select>
              <div className="">
                <label className="font-medium">From</label>
                <DatePicker
                  value={reportRequest.from}
                  clearable={true}
                  onChange={(from) =>
                    setReportRequest((prevState) => ({ ...prevState, from: from?.setZone(timezone).startOf('day') }))
                  }
                />
              </div>
              <div className="">
                <label className="font-medium">To</label>
                <DatePicker
                  value={reportRequest.to}
                  clearable={true}
                  onChange={(to) =>
                    setReportRequest((prevState) => ({ ...prevState, to: to?.setZone(timezone).endOf('day') }))
                  }
                />
              </div>
              <div className="">
                <label className="font-medium">Account Type</label>
                <SelectForm
                  className="h-fit text-[14px]"
                  value={selectedAccountType}
                  onChange={(e) => {
                    setReportRequest((prev) => ({ ...prev, accountType: e?.value }));
                  }}
                  options={accountTypeOptions}
                />
              </div>
              <div className="self-start">
                <label className="font-medium">Hide Location Totals</label>
                <div>
                  <Toggle
                    onClick={() => setReportRequest((prev) => ({ ...prev, hidePerLocation: !prev.hidePerLocation }))}
                    checked={reportRequest.hidePerLocation}
                  />
                </div>
              </div>
              {selectedAccountType?.value === GeneralLedgerAccountType.Tax && (
                <div className="self-start" data-cy="tax-reporting-basis">
                  <div className={'gap-2'}>
                    <label className="font-medium">Tax Reporting Basis</label>
                    <ButtonGroup<'cash' | 'accrual'>
                      items={buttonItems}
                      onClick={(value) => setReportRequest((prev) => ({ ...prev, basis: value as 'cash' | 'accrual' }))}
                      selected={reportRequest.basis}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                dataCy="create-account"
                className="button-yellow font-semibold"
                onClick={() => setAccountModalOpen(true)}
              >
                Create Account
              </Button>
              <Button dataCy="export" className="button-yellow font-semibold" onClick={exportReport}>
                Export
                <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
              </Button>
            </div>
          </div>
        </div>
      )}
      <Card>
        <table className="w-full">
          <thead>
            <tr>
              <th className="text-left">Account Number</th>
              <th className="text-left">Account Name</th>
              {!reportRequest.hidePerLocation && locations && locations?.length > 1 && (
                <th className="text-left">Location</th>
              )}
              {reportRequest.accountType == GeneralLedgerAccountType.Tax ? (
                <>
                  <th className="text-right" data-cy="revenue-total-header">
                    {reportRequest.accountType == GeneralLedgerAccountType.Tax ? 'Taxable Revenue' : 'Revenue Total'}
                  </th>
                  <th className="text-right" data-cy="total-header">
                    {reportRequest.accountType == GeneralLedgerAccountType.Tax ? 'Total Tax' : 'Total'}
                  </th>
                </>
              ) : (
                <th className="text-right">Total</th>
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={100} data-cy="loading-spinner">
                  <Spinner />
                </td>
              </tr>
            ) : (
              <>
                {currency && report && accounts && locations && (
                  <>
                    {report.length > 0 ? (
                      report.map((report, i) => {
                        return (
                          <TableRow
                            key={i}
                            account={accounts.find((acc) => acc.number === report['Account Number'])!}
                            report={report}
                            currency={currency}
                            locations={locations}
                            request={reportRequest}
                            onEdit={() => {
                              setAccountToUpdate(accounts.find((acc) => acc.number === report['Account Number']));
                              setAccountModalOpen(true);
                            }}
                            onDelete={() =>
                              setAccountToDelete(accounts.find((acc) => acc.number === report['Account Number']))
                            }
                          />
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7}>No record found</td>
                      </tr>
                    )}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </Card>
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Account Totals'} />
      {accountModalOpen && (
        <AccountModal
          isOpen={true}
          close={(isWrite) => {
            setAccountModalOpen(false);
            setAccountToUpdate(undefined);
            if (isWrite) {
              loadReport();
            }
          }}
          account={accountToUpdate}
        />
      )}
      {accountToDelete && (
        <ConfirmModal
          isOpen={true}
          close={() => setAccountToDelete(undefined)}
          onConfirm={async () => {
            try {
              await deleteAccount(accountToDelete.id);
              await loadReport();
              setAccountToDelete(undefined);
            } catch (e: any) {
              if (e.status === 400) {
                toast(e.message, 'error');
              } else {
                toast('Error deleting account', 'error');
              }
            }
          }}
          prompt={`Are you sure you want to delete account ${accountToDelete.number} - ${accountToDelete.name}?`}
        />
      )}
    </div>
  );
};

const TableRow = ({
  account,
  report,
  currency,
  locations,
  request,
  onEdit,
  onDelete,
}: {
  account: GeneralLedgerAccount;
  report: AccountTotalsRow;
  currency: string;
  locations: Location[];
  request: AccountTotalsRequest;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const [pageSizePreference] = useLocalStorage<number>('DefaultPageSize', 10);

  function toLedgerSearchParams(locationId?: number) {
    const params = new URLSearchParams();
    request.from && params.set('from', request.from.toISO());
    request.to && params.set('to', request.to.toISO());
    locationId && params.set('locationId', locationId.toString());
    request.basis && account.accountType == 'Tax' && params.set('basis', request.basis);
    params.set('pageSize', pageSizePreference.toString());
    params.set('pageNumber', '1');
    return params.toString();
  }

  if (report['Location Totals'].length > 1 && !request.hidePerLocation && locations.length > 1) {
    return (
      <>
        {report['Location Totals'].map((lt, i) => (
          <tr key={i}>
            <td>{i === 0 ? report['Account Number'] : ''}</td>
            <td>
              {i === 0 && (
                <div className="flex gap-2 items-center account-name">
                  <span>
                    <Link to={`${account.id}?${toLedgerSearchParams()}`}>{report['Account Name']}</Link>
                  </span>
                  <div className="flex gap-1 account-actions">
                    <Button dataCy="edit-account" className="button-tertiary account-button" onClick={onEdit}>
                      <Icon icon={'Edit'} stroke="var(--grey-400)" />
                    </Button>
                    <Button dataCy="delete-account" className="button-tertiary account-button" onClick={onDelete}>
                      <Icon icon={'Trash'} stroke="var(--grey-400)" />
                    </Button>
                  </div>
                </div>
              )}
            </td>
            <td>
              <Link to={`${account.id}?${toLedgerSearchParams(locations.find((l) => l.name === lt.Location)?.id)}`}>
                {lt.Location}
              </Link>
            </td>
            {request.accountType == GeneralLedgerAccountType.Tax ? (
              <>
                <td className="text-right">{displayMoneyFormat(lt['Revenue Total'], currency)}</td>
                <td className="text-right">{displayMoneyFormat(lt['Total'], currency)}</td>
              </>
            ) : (
              <td className="text-right">{displayMoneyFormat(lt['Total'], currency)}</td>
            )}
          </tr>
        ))}
        <tr>
          <td></td>
          <td></td>
          <td className="text-right font-semibold">Total</td>
          {request.accountType == GeneralLedgerAccountType.Tax ? (
            <>
              <td className="text-right">{displayMoneyFormat(report['Revenue Total'], currency)}</td>
              <td className="text-right font-semibold">{displayMoneyFormat(report['Total'], currency)}</td>
            </>
          ) : (
            <td className="text-right font-semibold">{displayMoneyFormat(report['Total'], currency)}</td>
          )}
        </tr>
        <tr className={'h-[2.5rem]'}></tr>
      </>
    );
  } else {
    return (
      <tr>
        <td>{report['Account Number']}</td>
        <td>
          <div className="flex gap-2 items-center account-name">
            <span>
              <Link to={`${account.id}?${toLedgerSearchParams()}`}>{report['Account Name']}</Link>
            </span>
            <div className="flex gap-1 account-actions">
              <Button className="button-tertiary account-button" onClick={onEdit}>
                <Icon icon={'Edit'} stroke="var(--grey-400)" />
              </Button>
              <Button className="button-tertiary account-button" onClick={onDelete}>
                <Icon icon={'Trash'} stroke="var(--grey-400)" />
              </Button>
            </div>
          </div>
        </td>
        {request.accountType == GeneralLedgerAccountType.Tax ? (
          <>
            <td className="text-right">{displayMoneyFormat(report['Revenue Total'], currency)}</td>
            <td className="text-right font-semibold">{displayMoneyFormat(report['Total'], currency)}</td>
          </>
        ) : (
          <td className="text-right font-semibold">{displayMoneyFormat(report['Total'], currency)}</td>
        )}
      </tr>
    );
  }
};
